/**
 * approvalConfetti
 * ================
 * Tiny celebratory burst when a customer authorises work in the
 * portal — boosts the moment of commitment without being gimmicky.
 *
 *  • ``itemApprovalBurst()``  — short, restrained burst from the
 *    Approve button, intended for per-item approvals.
 *  • ``bulkApprovalBurst()``  — larger, slightly wider burst when
 *    the customer picks "Complete all recommended work".
 *
 * Both honour ``prefers-reduced-motion`` so accessibility-sensitive
 * users never see motion they didn't ask for. Both are no-ops on
 * SSR (window-guarded) and degrade silently if the library fails
 * to import (the portal must keep working even if confetti is
 * blocked by an extension).
 */
import confetti from "canvas-confetti";

const _prefersReducedMotion = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
};

const COLOURS_GREEN = ["#10b981", "#34d399", "#6ee7b7", "#a7f3d0", "#fbbf24"];

/**
 * Single-item approval — fires a thin upward burst from below the
 * Approve button. The element is optional; when missing, the burst
 * originates from just above the bottom of the viewport, which is
 * where the customer's thumb lives on mobile.
 */
export const itemApprovalBurst = (targetEl) => {
  if (typeof window === "undefined") return;
  if (_prefersReducedMotion()) return;
  try {
    let originX = 0.5;
    let originY = 0.85;
    if (targetEl && targetEl.getBoundingClientRect) {
      const rect = targetEl.getBoundingClientRect();
      originX = (rect.left + rect.width / 2) / window.innerWidth;
      originY = (rect.top + rect.height / 2) / window.innerHeight;
    }
    confetti({
      particleCount: 40,
      spread:        55,
      startVelocity: 38,
      ticks:         110,
      origin:        { x: originX, y: originY },
      colors:        COLOURS_GREEN,
      scalar:        0.85,
      zIndex:        9999,
    });
  } catch (e) {
    // Silent — confetti must never break the approval flow.
  }
};

/**
 * Bulk approval — slightly bigger, originates lower so the burst
 * reads as "all done" rather than "one item". Used by the "Complete
 * all recommended work" CTA.
 */
export const bulkApprovalBurst = () => {
  if (typeof window === "undefined") return;
  if (_prefersReducedMotion()) return;
  try {
    confetti({
      particleCount: 90,
      spread:        80,
      startVelocity: 45,
      ticks:         140,
      origin:        { x: 0.5, y: 0.7 },
      colors:        COLOURS_GREEN,
      scalar:        0.95,
      zIndex:        9999,
    });
    // Second smaller pop a moment later so it feels like a real
    // celebration rather than a single thud.
    setTimeout(() => {
      confetti({
        particleCount: 50,
        spread:        100,
        startVelocity: 30,
        ticks:         110,
        origin:        { x: 0.5, y: 0.55 },
        colors:        COLOURS_GREEN,
        scalar:        0.75,
        zIndex:        9999,
      });
    }, 180);
  } catch (e) {
    // Silent.
  }
};
